import { api } from './client';
import type { Page } from './catalogs';

export type TaxType = 'PDFO' | 'WZ' | 'ESV';

export const TAX_TYPE_LABELS: Record<TaxType, string> = {
  PDFO: 'ПДФО',
  WZ: 'Військовий збір',
  ESV: 'ЄСВ',
};

export interface TaxRemittanceItem {
  id: string;
  line_number: number;
  tax_type: TaxType;
  account_code: string;
  amount: string;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  payroll_id: string | null;
  payment_purpose: string | null;
  activity_place_id: string | null;  // ч.806
}

export interface TaxRemittanceDoc {
  id: string;
  doc_type: string;
  doc_id: string;
  line_number?: number | null;
}

export interface TaxRemittance {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  bank_account_id: string | null;
  // Виплата-джерело («На основі виплати»).
  base_payment_id: string | null;
  total_pdfo: string;
  total_wz: string;
  total_esv: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  state: string;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  comment: string | null;
  items: TaxRemittanceItem[];
  payment_docs: TaxRemittanceDoc[];
}

export interface TaxRemittanceListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  total_pdfo: string;
  total_wz: string;
  total_esv: string;
  posted: boolean;
  deletion_mark: boolean;
  state: string;
}

export interface TaxRemittanceItemIn {
  tax_type: TaxType;
  account_code: string;
  amount: string;
  recipient_name?: string | null;
  recipient_edrpou?: string | null;
  recipient_iban?: string | null;
  payroll_id?: string | null;
  source_bank_account_id?: string | null;
  funding_source?: string | null;
  payment_purpose?: string | null;
  // ч.806: ПДФО відокремленого підрозділу (рахунок місцевого бюджету місця).
  activity_place_id?: string | null;
}

export interface FromPaymentRow {
  tax_type: TaxType;
  account_code: string;
  amount: string;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  payment_purpose: string | null;
  activity_place_id?: string | null;   // ч.806
  activity_place_name?: string | null; // ч.806
}
export interface FromPaymentResult {
  organization_id: string;
  period_year: number;
  period_month: number;
  bank_account_id: string | null;
  source: string;
  // true — податки пропорційні фактично виплаченому (часткова виплата).
  scaled: boolean;
  // true — суми зменшено залишком боргу бюджету (частину вже перераховано).
  capped: boolean;
  rows: FromPaymentRow[];
}

export interface TaxRemittanceCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  period_year: number;
  period_month: number;
  bank_account_id?: string | null;
  base_payment_id?: string | null;
  comment?: string | null;
  items: TaxRemittanceItemIn[];
}

export type TaxRemittanceUpdate = Partial<Omit<TaxRemittanceCreate, 'organization_id'>>;

export interface FetchFromPayrollResult {
  period_year: number;
  period_month: number;
  total_pdfo: string;
  total_wz: string;
  total_esv: string;
  pdfo_account_code: string;
  wz_account_code: string;
  esv_account_code: string;
  pdfo_recipient_name: string | null;
  pdfo_recipient_edrpou: string | null;
  pdfo_recipient_iban: string | null;
  wz_recipient_name: string | null;
  wz_recipient_edrpou: string | null;
  wz_recipient_iban: string | null;
  esv_recipient_name: string | null;
  esv_recipient_edrpou: string | null;
  esv_recipient_iban: string | null;
  // ч.806: ПДФО по місцях діяльності окремими рядками (total_pdfo — основне місце).
  place_pdfo_rows: FromPaymentRow[];
}

export const taxRemittances = {
  list: (params?: { organization_id?: string; period_year?: number; period_month?: number; period_from?: string; period_to?: string; posted?: boolean; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<TaxRemittanceListItem>>('/tax-remittances', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TaxRemittance>(`/tax-remittances/${id}`).then((r) => r.data),
  create: (payload: TaxRemittanceCreate) =>
    api.post<TaxRemittance>('/tax-remittances', payload).then((r) => r.data),
  update: (id: string, payload: TaxRemittanceUpdate) =>
    api.patch<TaxRemittance>(`/tax-remittances/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-remittances/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TaxRemittance>(`/tax-remittances/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<TaxRemittance>(`/tax-remittances/${id}/unpost`).then((r) => r.data),
  // ч.765: платіжні інструкції — явною дією після проведення (чернетки).
  createBankPayments: (id: string) =>
    api.post<TaxRemittance>(`/tax-remittances/${id}/create-bank-payments`).then((r) => r.data),
  fetchFromPayroll: (params: {
    organization_id: string; period_year: number; period_month: number;
  }) =>
    api.get<FetchFromPayrollResult>('/tax-remittances/fetch-from-payroll', { params })
      .then((r) => r.data),
  fromPayment: (params: { organization_id: string; payroll_payment_id: string }) =>
    api.get<FromPaymentResult>('/tax-remittances/from-payment', { params }).then((r) => r.data),
};
